// @ts-check
import { Component } from '@theme/component';

const OPEN_DELAY = 80;
const CLOSE_DELAY = 100;

/** Desktop mega menu: items open their panel on hover (intent delay) or keyboard focus. `on:`
 * delegates pointerenter/leave, but not focusin/focusout (don't bubble), so those bind manually. */
class CustomMegaMenu extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #openTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  #suppressFocusOpen = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    // On document: a panel opened by hover has to close on Escape while focus is elsewhere.
    document.addEventListener('keydown', this.#handleKeydown);

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', this.#handleBlockSelect);
      this.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);

    // Mirror the connectedCallback binds so listeners don't stack when the element reconnects.
    // Handlers are stable arrow-fn fields, so removing the unbound designMode pair is a no-op.
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    document.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
  }

  /**
   * Opens an item's panel after a short hover-intent delay. When another
   * panel is already open, hovering an adjacent dropdown item swaps panels
   * instantly (no exit/re-entry animation); hovering a plain-link item only
   * closes the open panel after the usual delay, so passing across it on the
   * way to another item doesn't flicker.
   * Bound declaratively via `on:pointerenter="/open"` on the item.
   * @param {PointerEvent} event
   */
  open(event) {
    const item = this.#itemFromEvent(event);
    if (!item) return;

    clearTimeout(this.#closeTimer);
    clearTimeout(this.#openTimer);

    const expanded = this.#expandedItem();

    if (!item.querySelector('.custom-mega-menu__panel')) {
      if (expanded) {
        this.#closeTimer = setTimeout(() => this.#closeAll(), CLOSE_DELAY);
      }
      return;
    }

    if (expanded && expanded !== item) {
      this.#swapTo(item);
    } else if (!expanded) {
      this.#openTimer = setTimeout(() => this.#openItem(item), OPEN_DELAY);
    }
  }

  /** Closes the open panel after a short delay (cancelled if the pointer re-enters). Bound
   * declaratively via `on:pointerleave="/close"`. */
  close() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#closeAll(), CLOSE_DELAY);
  }

  /**
   * Opens the panel of the item that received keyboard focus.
   * @param {FocusEvent} event
   */
  #handleFocusIn = (event) => {
    if (this.#suppressFocusOpen) return;
    const item = this.#itemFromEvent(event);
    if (!item) return;

    clearTimeout(this.#closeTimer);

    const expanded = this.#expandedItem();
    if (expanded && expanded !== item) {
      this.#swapTo(item);
    } else {
      this.#openItem(item);
    }
  };

  /**
   * Closes everything when focus leaves the menu entirely.
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.#closeAll();
  };

  /**
   * Closes the open panel on Escape, or one still waiting out its hover delay, and restores focus
   * to its top link.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const expandedLink = this.querySelector('.custom-mega-menu__top-link[aria-expanded="true"]');
    const hadFocus = this.contains(document.activeElement);
    this.#closeAll();
    if (!(expandedLink instanceof HTMLElement) || !hadFocus) return;
    // Focusing the link fires focusin, which would reopen the panel just closed.
    this.#suppressFocusOpen = true;
    expandedLink.focus();
    this.#suppressFocusOpen = false;
  };

  /**
   * Theme editor: opens the panel containing the selected nested block.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const item = this.#itemFromEvent(event);
    if (item) this.#openItem(item);
  };

  #handleBlockDeselect = () => {
    this.#closeAll();
  };

  /**
   * Resolves the top-level `<li>` containing an event's target.
   * @param {Event} event
   * @returns {Element | null}
   */
  #itemFromEvent(event) {
    const { target } = event;
    if (!(target instanceof Element)) return null;
    return target.closest('.custom-mega-menu__item');
  }

  /**
   * Returns the item whose panel is currently expanded, if any.
   * @returns {Element | null}
   */
  #expandedItem() {
    const link = this.querySelector('.custom-mega-menu__top-link[aria-expanded="true"]');
    return link ? link.closest('.custom-mega-menu__item') : null;
  }

  /**
   * Swaps the open panel to `item` with transitions suppressed, so moving
   * between adjacent top-level items doesn't re-run the open/close animation.
   * @param {Element} item
   */
  #swapTo(item) {
    this.classList.add('custom-mega-menu--switching');
    this.#openItem(item);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.remove('custom-mega-menu--switching'));
    });
  }

  /**
   * Opens `item`'s panel (if it has one) and closes all others.
   * @param {Element} item
   */
  #openItem(item) {
    for (const other of this.querySelectorAll('.custom-mega-menu__item')) {
      if (other !== item) this.#setExpanded(other, false);
    }
    this.#setExpanded(item, true);
  }

  #closeAll() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    for (const item of this.querySelectorAll('.custom-mega-menu__item')) {
      this.#setExpanded(item, false);
    }
  }

  /**
   * Toggles an item's `aria-expanded` link state and panel visibility.
   * @param {Element} item
   * @param {boolean} expanded
   */
  #setExpanded(item, expanded) {
    const link = item.querySelector('.custom-mega-menu__top-link[aria-controls]');
    const panel = item.querySelector('.custom-mega-menu__panel');
    if (!link || !panel) return;

    link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    panel.toggleAttribute('hidden', !expanded);
  }
}

if (!customElements.get('custom-mega-menu')) {
  customElements.define('custom-mega-menu', CustomMegaMenu);
}
